/**
 * WebHID-basierter Smartcard Reader
 * Funktioniert mit NFC-Readern, die als HID-Gerät arbeiten (Keyboard Emulation)
 * Keine Installation notwendig - läuft direkt im Browser!
 */

interface SmartcardReaderHIDCallbacks {
  onScan: (code: string) => void;
  onError: (error: string) => void;
  onConnected: () => void;
  onDisconnected: () => void;
}

export class SmartcardReaderHID {
  private device: HIDDevice | null = null;
  private callbacks: SmartcardReaderHIDCallbacks;
  private buffer: string = '';
  private scanTimeout: NodeJS.Timeout | null = null;
  
  // Trust Ceto USB Vendor/Product IDs
  public static readonly FILTERS = [
    { vendorId: 0xae68, productId: 0x8001 }, // Trust Ceto (alternate)
    { vendorId: 0x17ef, productId: 0x30ca }, // Trust Ceto (main)
    { vendorId: 0x072f }, // ACS Readers
    { vendorId: 0x076b }, // OmniKey Readers
    // Generic HID - wird vom Benutzer ausgewählt
  ];

  constructor(callbacks: SmartcardReaderHIDCallbacks) {
    this.callbacks = callbacks;
  }

  async connect(): Promise<boolean> {
    try {
      if (!('hid' in navigator)) {
        throw new Error('WebHID wird von diesem Browser nicht unterstützt. Nutze Chrome/Edge.');
      }

      console.log('🔗 WebHID: Fordere Geräteauswahl-Dialog an...');
      
      // Gerät vom Benutzer auswählen lassen
      const devices = await (navigator as any).hid.requestDevice({
        filters: SmartcardReaderHID.FILTERS
      });

      if (devices.length === 0) {
        throw new Error('Kein Gerät ausgewählt');
      }

      this.device = devices[0];
      console.log('✅ WebHID: Gerät ausgewählt:', {
        name: this.device.productName,
        vendor: `0x${this.device.vendorId.toString(16)}`,
        product: `0x${this.device.productId.toString(16)}`
      });

      // Gerät öffnen
      if (!this.device.opened) {
        await this.device.open();
        console.log('📂 Gerät geöffnet');
      }

      // Event-Listener für Eingaben
      this.device.addEventListener('inputreport', this.handleInputReport.bind(this));

      this.callbacks.onConnected();
      return true;

    } catch (err: any) {
      console.error('❌ WebHID Fehler:', err);
      this.callbacks.onError(`WebHID Fehler: ${err.message}`);
      return false;
    }
  }

  private handleInputReport(event: any) {
    try {
      const { data } = event;
      
      // HID Input Report als Uint8Array
      const bytes = new Uint8Array(data.buffer);
      
      // Konvertiere zu String (viele NFC-Reader emulieren Keyboard)
      // Die meisten senden ASCII-Zeichen
      let text = '';
      for (let i = 0; i < bytes.length; i++) {
        const byte = bytes[i];
        if (byte >= 32 && byte <= 126) { // Druckbare ASCII-Zeichen
          text += String.fromCharCode(byte);
        } else if (byte === 13 || byte === 10) { // Enter/Return
          // Scan komplett
          if (this.buffer.length > 0) {
            this.processScan(this.buffer);
            this.buffer = '';
          }
        }
      }

      if (text.length > 0) {
        this.buffer += text;
        
        // Timeout: Nach 500ms ohne neue Daten wird Buffer als Scan behandelt
        if (this.scanTimeout) {
          clearTimeout(this.scanTimeout);
        }
        this.scanTimeout = setTimeout(() => {
          if (this.buffer.length > 0) {
            this.processScan(this.buffer);
            this.buffer = '';
          }
        }, 500);
      }

    } catch (err) {
      console.error('❌ Fehler beim Verarbeiten der HID-Daten:', err);
    }
  }

  private processScan(code: string) {
    // Bereinige den Code (trim whitespace)
    const cleanCode = code.trim();
    
    if (cleanCode.length > 0) {
      console.log('🎫 Karte gescannt (HID):', cleanCode);
      this.callbacks.onScan(cleanCode);
    }
  }

  async disconnect(): Promise<void> {
    try {
      if (this.scanTimeout) {
        clearTimeout(this.scanTimeout);
      }

      if (this.device) {
        if (this.device.opened) {
          await this.device.close();
          console.log('📪 Gerät geschlossen');
        }
        this.device = null;
      }

      this.callbacks.onDisconnected();
    } catch (err) {
      console.error('❌ Fehler beim Trennen:', err);
    }
  }

  isConnected(): boolean {
    return this.device !== null && this.device.opened;
  }

  getDeviceInfo(): { name: string; vendor: string; product: string } | null {
    if (!this.device) return null;

    return {
      name: this.device.productName || 'Unbekannt',
      vendor: `0x${this.device.vendorId.toString(16).padStart(4, '0')}`,
      product: `0x${this.device.productId.toString(16).padStart(4, '0')}`
    };
  }
}

// Alternative: Raw HID Data Reader für Reader die nicht Keyboard emulieren
export class SmartcardReaderHIDRaw {
  private device: HIDDevice | null = null;
  private callbacks: SmartcardReaderHIDCallbacks;
  
  constructor(callbacks: SmartcardReaderHIDCallbacks) {
    this.callbacks = callbacks;
  }

  async connect(): Promise<boolean> {
    try {
      if (!('hid' in navigator)) {
        throw new Error('WebHID nicht unterstützt');
      }

      const devices = await (navigator as any).hid.requestDevice({
        filters: SmartcardReaderHID.FILTERS
      });

      if (devices.length === 0) return false;

      this.device = devices[0];
      await this.device.open();

      // Versuche verschiedene Report IDs zu lesen
      this.device.addEventListener('inputreport', (event: any) => {
        const { data } = event;
        const bytes = new Uint8Array(data.buffer);
        
        // Extrahiere UID aus Raw-Daten
        // ISO 14443 UIDs sind typischerweise 4, 7 oder 10 Bytes
        const uid = this.extractUID(bytes);
        if (uid) {
          this.callbacks.onScan(uid);
        }
      });

      this.callbacks.onConnected();
      return true;

    } catch (err: any) {
      console.error('❌ WebHID Raw Fehler:', err);
      this.callbacks.onError(err.message);
      return false;
    }
  }

  private extractUID(bytes: Uint8Array): string | null {
    // Suche nach UID-Pattern in den Daten
    // Dies ist sehr Reader-spezifisch und muss angepasst werden
    
    if (bytes.length < 4) return null;

    // Konvertiere zu Hex-String
    const hex = Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    // Filtere nur wenn es wie eine UID aussieht (nicht nur Nullen)
    if (hex.replace(/0/g, '').length > 0) {
      return `NFC-${hex.toUpperCase()}`;
    }

    return null;
  }

  async disconnect(): Promise<void> {
    if (this.device?.opened) {
      await this.device.close();
    }
    this.device = null;
    this.callbacks.onDisconnected();
  }
}
